import React, { useState, memo } from 'react';
import { formatCurrency, sanitizeAmount } from '../helpers';

const TASAS_IVA = [
    { label: '21.0% (General)', value: 21 },
    { label: '10.5% (Reducida)', value: 10.5 },
    { label: '27.0% (Servicios)', value: 27 },
    { label: '0.0% (Sin IVA / Exento)', value: 0 }
];

const TIPOS_PERCEPCIONES = [
    'IVA',
    'IIBB',
    'Otras Percepciones'
];

const SeccionCalculoImportes = ({
    formData,
    setFormData,
    calculos,
    esImpuesto,
    handleBlurCurrency,
    formatBlurInputAmount
}) => {
    const esPrestamo = formData.tipoComprobante === 'Préstamo';
    const esModoDirecto = esImpuesto || esPrestamo;

    const monedaActual = formData.moneda || 'ARS';
    const simboloMoneda = monedaActual === 'USD' ? 'U$S' : '$';
    const monedaSimbolo = monedaActual === 'USD' ? 'USD' : 'ARS';

    const [tipoPercepcionInput, setTipoPercepcionInput] = useState('');
    const [montoPercepcionInput, setMontoPercepcionInput] = useState('');

    const handleAddPercepcion = () => {
        if (!tipoPercepcionInput) return;
        const numMonto = sanitizeAmount(montoPercepcionInput);
        if (numMonto <= 0) return;

        setFormData(prev => {
            const listaActual = Array.isArray(prev.percepcionesList) ? prev.percepcionesList : [];
            return {
                ...prev,
                percepcionesList: [...listaActual, { tipo: tipoPercepcionInput, monto: numMonto }]
            };
        });

        setTipoPercepcionInput('');
        setMontoPercepcionInput('');
    };

    const handleRemovePercepcion = (index) => {
        setFormData(prev => {
            const listaActual = Array.isArray(prev.percepcionesList) ? prev.percepcionesList : [];
            return {
                ...prev,
                percepcionesList: listaActual.filter((_, idx) => idx !== index)
            };
        });
    };

    const percepcionesList = Array.isArray(formData.percepcionesList) ? formData.percepcionesList : [];

    return (
        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                {esImpuesto 
                    ? 'Cálculo de Impuestos' 
                    : esPrestamo 
                        ? 'Cálculo de Préstamo' 
                        : 'Cálculo del Gasto e Impuestos'}
            </legend>

            <div className="space-y-5 pt-2">
                {esModoDirecto ? (
                    /* MODO DIRECTO: IMPUESTOS O PRÉSTAMOS */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        <div>
                            <label className="block text-[14px] font-black text-indigo-900 mb-1.5 pl-1">
                                {esPrestamo ? `Total del Préstamo (${simboloMoneda}) *` : `Total Impuesto (${simboloMoneda}) *`}
                            </label>
                            
                            <div className="flex gap-2">
                                <select
                                    value={formData.moneda === 'USD' ? 'USD U$S' : 'ARS $'}
                                    onChange={e => {
                                        const val = e.target.value.includes('USD') ? 'USD' : 'ARS';
                                        setFormData(prev => ({ ...prev, moneda: val }));
                                    }}
                                    className="w-32 border-2 border-indigo-200 bg-indigo-50/50 rounded-xl px-3 py-2.5 text-sm font-bold text-indigo-950 outline-none h-[48px] focus:border-indigo-400 cursor-pointer"
                                >
                                    <option value="ARS $">ARS $</option>
                                    <option value="USD U$S">USD U$S</option>
                                </select>

                                <input
                                    type="text"
                                    required
                                    value={formData.montoNeto || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, montoNeto: e.target.value }))}
                                    onBlur={() => handleBlurCurrency('montoNeto')}
                                    className="flex-grow border-2 border-indigo-200 bg-indigo-50/30 rounded-xl px-4 py-2.5 text-base font-mono font-black text-indigo-950 outline-none h-[48px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all"
                                    placeholder="0,00"
                                />
                            </div>
                        </div>

                        <div className="bg-indigo-50/60 p-4 rounded-xl border border-indigo-100 flex flex-col justify-center">
                            <span className="text-[10px] font-black text-indigo-800 uppercase tracking-wider">
                                {esPrestamo ? 'Monto Total del Préstamo' : 'Monto Total del Impuesto'}
                            </span>
                            <span className="font-mono text-xl font-black text-indigo-950 mt-0.5">
                                {formatCurrency(calculos.montoTotal, monedaSimbolo)}
                            </span>
                        </div>
                    </div>
                ) : (
                    /* MODO ESTÁNDAR: GASTOS CON IVA, PERCEPCIONES Y TRIBUTOS */
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Monto Neto ({simboloMoneda}) *</label>
                                
                                <div className="flex gap-2">
                                    <select
                                        value={formData.moneda === 'USD' ? 'USD U$S' : 'ARS $'}
                                        onChange={e => {
                                            const val = e.target.value.includes('USD') ? 'USD' : 'ARS';
                                            setFormData(prev => ({ ...prev, moneda: val }));
                                        }}
                                        className="w-32 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-800 outline-none h-[46px] focus:border-blue-400 cursor-pointer"
                                    >
                                        <option value="ARS $">ARS $</option>
                                        <option value="USD U$S">USD U$S</option>
                                    </select>

                                    <input
                                        type="text"
                                        value={formData.montoNeto || ''}
                                        onChange={e => setFormData(prev => ({ ...prev, montoNeto: e.target.value }))}
                                        onBlur={() => handleBlurCurrency('montoNeto')}
                                        className="flex-grow border border-slate-200 rounded-xl px-4 py-2.5 text-base font-mono font-bold text-slate-900 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                        placeholder="0,00"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tasa de IVA (%)</label>
                                <select
                                    value={formData.tasaIva}
                                    onChange={e => setFormData(prev => ({ ...prev, tasaIva: e.target.value }))}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all cursor-pointer"
                                >
                                    {TASAS_IVA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* TOGGLES / CARDS OPCIONALES */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            {/* CARD 1: PERCEPCIONES */}
                            <div 
                                onClick={() => setFormData(prev => ({
                                    ...prev,
                                    incluyePercepciones: !prev.incluyePercepciones,
                                    percepcionesList: !prev.incluyePercepciones ? (prev.percepcionesList || []) : []
                                }))}
                                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between select-none ${
                                    formData.incluyePercepciones
                                        ? 'bg-indigo-50/80 border-indigo-300 ring-2 ring-indigo-500/20 shadow-xs'
                                        : 'bg-slate-50/80 border-slate-200 hover:bg-slate-100/80 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                                        formData.incluyePercepciones 
                                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-xs' 
                                            : 'bg-white border-slate-300'
                                    }`}>
                                        {formData.incluyePercepciones && (
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-wide flex items-center gap-1.5">
                                            🏛️ Percepciones Sufridas
                                        </span>
                                        <span className="text-[10px] font-semibold text-slate-500">
                                            IVA, IIBB o percepciones de terceros
                                        </span>
                                    </div>
                                </div>

                                <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border transition-all ${
                                    formData.incluyePercepciones
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white text-slate-400 border-slate-200'
                                }`}>
                                    {formData.incluyePercepciones ? 'Habilitado' : 'No incluye'}
                                </span>
                            </div>

                            {/* CARD 2: OTROS TRIBUTOS */}
                            <div 
                                onClick={() => setFormData(prev => ({
                                    ...prev,
                                    incluyeOtrosTributos: !prev.incluyeOtrosTributos,
                                    montoOtrosTributos: !prev.incluyeOtrosTributos ? prev.montoOtrosTributos : ''
                                }))}
                                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between select-none ${
                                    formData.incluyeOtrosTributos
                                        ? 'bg-purple-50/80 border-purple-300 ring-2 ring-purple-500/20 shadow-xs'
                                        : 'bg-slate-50/80 border-slate-200 hover:bg-slate-100/80 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                                        formData.incluyeOtrosTributos 
                                            ? 'bg-purple-600 border-purple-600 text-white shadow-xs' 
                                            : 'bg-white border-slate-300'
                                    }`}>
                                        {formData.incluyeOtrosTributos && (
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-wide flex items-center gap-1.5">
                                            📑 Impuestos / Tasas
                                        </span>
                                        <span className="text-[10px] font-semibold text-slate-500">
                                            Tasas municipales, servicios o no gravados
                                        </span>
                                    </div>
                                </div>

                                <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border transition-all ${
                                    formData.incluyeOtrosTributos
                                        ? 'bg-purple-600 text-white border-purple-600'
                                        : 'bg-white text-slate-400 border-slate-200'
                                }`}>
                                    {formData.incluyeOtrosTributos ? 'Habilitado' : 'No incluye'}
                                </span>
                            </div>

                        </div>

                        {/* DESPLEGABLE PERCEPCIONES */}
                        {formData.incluyePercepciones && (
                            <div className="p-4 bg-indigo-50/35 rounded-xl border border-indigo-100 space-y-3 animate-fade-in shadow-2xs">
                                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span className="text-xs font-black text-indigo-800 uppercase tracking-wider">
                                        Desglose de Percepciones
                                    </span>
                                    {calculos.totalPercepciones > 0 && (
                                        <span className="text-xs font-mono font-bold text-indigo-900 bg-indigo-100/80 px-2.5 py-0.5 rounded-lg border border-indigo-200">
                                            Total: {formatCurrency(calculos.totalPercepciones, monedaSimbolo)}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                    <div>
                                        <label className="block text-[12px] font-bold text-slate-600 uppercase mb-1">Tipo de Percepción</label>
                                        <select
                                            value={tipoPercepcionInput}
                                            onChange={e => setTipoPercepcionInput(e.target.value)}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-800 bg-white h-[42px] outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all cursor-pointer"
                                        >
                                            <option value="">Seleccionar Tipo...</option>
                                            {TIPOS_PERCEPCIONES.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-[12px] font-bold text-slate-600 uppercase mb-1">Monto ({simboloMoneda})</label>
                                        <input
                                            type="text"
                                            value={montoPercepcionInput}
                                            onChange={e => setMontoPercepcionInput(e.target.value)}
                                            onBlur={() => formatBlurInputAmount && formatBlurInputAmount(montoPercepcionInput, setMontoPercepcionInput)}
                                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-base font-mono font-bold text-slate-900 bg-white h-[42px] outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all"
                                            placeholder="0,00"
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleAddPercepcion}
                                        className="h-[42px] px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold uppercase text-xs transition-all shrink-0 cursor-pointer shadow-xs active:scale-98"
                                    >
                                        + Agregar Percepción
                                    </button>
                                </div>

                                {percepcionesList.length > 0 && (
                                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200/60">
                                        {percepcionesList.map((perc, idx) => (
                                            <span key={idx} className="inline-flex items-center gap-2 bg-white border border-indigo-200 text-slate-800 font-bold px-3 py-1.5 rounded-xl text-xs shadow-2xs">
                                                <span className="text-indigo-900 uppercase">Percepción {perc.tipo}:</span>
                                                <span className="font-mono text-sm font-bold text-slate-900">{formatCurrency(perc.monto, monedaSimbolo)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePercepcion(idx)}
                                                    className="text-rose-400 hover:text-rose-700 font-black ml-1 text-sm transition-colors cursor-pointer"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* DESPLEGABLE OTROS TRIBUTOS */}
                        {formData.incluyeOtrosTributos && (
                            <div className="p-4 bg-purple-50/35 rounded-xl border border-purple-100 space-y-2 animate-fade-in shadow-2xs">
                                <label className="block text-xs font-black text-purple-900 uppercase tracking-wider">
                                    Monto Impuestos Internos / Tasas / Conceptos No Gravados ({simboloMoneda})
                                </label>
                                <input
                                    type="text"
                                    value={formData.montoOtrosTributos || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, montoOtrosTributos: e.target.value }))}
                                    onBlur={() => handleBlurCurrency('montoOtrosTributos')}
                                    className="w-full border border-purple-200 rounded-xl px-4 py-2.5 text-base font-mono font-bold text-slate-900 bg-white h-[46px] outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100 transition-all"
                                    placeholder="0,00"
                                />
                            </div>
                        )}

                        {/* RESUMEN DE TOTALES */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                            <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-300 flex flex-col justify-center">
                                <span className="text-[10px] font-black text-blue-800 uppercase tracking-wider">Monto IVA</span>
                                <span className="font-mono text-base font-bold text-blue-800 mt-1">{formatCurrency(calculos.montoIva, monedaSimbolo)}</span>
                            </div>

                            <div className="bg-indigo-50/70 p-4 rounded-xl border border-indigo-300 flex flex-col justify-center">
                                <span className="text-[10px] font-black text-indigo-800 uppercase tracking-wider">Total Percepciones</span>
                                <span className="font-mono text-base font-bold text-indigo-800 mt-1">
                                    {formatCurrency(calculos.totalPercepciones, monedaSimbolo)}
                                </span>
                            </div>

                            <div className="bg-purple-50/70 p-4 rounded-xl border border-purple-300 flex flex-col justify-center">
                                <span className="text-[10px] font-black text-purple-800 uppercase tracking-wider">Otros Tributos</span>
                                <span className="font-mono text-base font-bold text-purple-800 mt-1">
                                    {formatCurrency(calculos.montoOtrosTributos, monedaSimbolo)}
                                </span>
                            </div>

                            <div className="bg-rose-50/70 p-4 rounded-xl border border-rose-200 flex flex-col justify-center">
                                <span className="text-[10px] font-black text-rose-700 uppercase tracking-wider">Monto Total Factura</span>
                                <span className="font-mono text-lg font-black text-rose-800 mt-1">{formatCurrency(calculos.montoTotal, monedaSimbolo)}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </fieldset>
    );
};

export default memo(SeccionCalculoImportes);